import './style.scss'
import { Badge, Popover, Table } from 'antd'
import dayjs from 'dayjs'
import { FC, memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserStore } from '@/global-states'
import { InfoCircleOutlined } from '@ant-design/icons'

const PointDetail: FC = () => {
  const { t: translate } = useTranslation(['personal'])
  const { creditLog, productOrder, pointOrder, payStatus, loginStatus } = UserStore
  const [tabIndx, setTabIndx] = useState(0)

  const tableData = useMemo(() => {
    if (tabIndx === 1) {
      return productOrder
    }
    if (tabIndx === 2) {
      return pointOrder
    }
    return creditLog
  }, [tabIndx, creditLog, productOrder, pointOrder])

  const onPageChange = (currentPage: number, currentPageSize: number) => {
    getData(currentPage - 1, currentPageSize)
  }

  useEffect(() => {
    getData()
  }, [tabIndx])

  useEffect(() => {
    if (payStatus || loginStatus) {
      getData()
    }
  }, [payStatus, loginStatus])

  const getData = (page?: number, pageSize?: number) => {
    if (tabIndx === 0) {
      UserStore.getCreditLogs(page, pageSize)
    }
    if (tabIndx === 1) {
      UserStore.getProductOrders(page, pageSize)
    }
    if (tabIndx === 2) {
      UserStore.getPointOrders(page, pageSize)
    }
  }

  const columns = useMemo(() => {
    if (tabIndx === 0) {
      return [
        {
          title: translate('personal.type'),
          dataIndex: 'description',
          width: 300
        },
        {
          title: translate('personal.points_change'),
          dataIndex: 'credits_delta',
          render: (num: number) => <div>{num > 0 ? `+${num}` : !num ? 0 : num}</div>,
          width: 300
        },
        {
          title: translate('personal.time'),
          dataIndex: 'create_time',
          width: 300,
          render: (time: number) => <div>{dayjs(time * 1000).format('YYYY-MM-DD HH:mm')}</div>
        }
      ]
    } else {
      return [
        {
          title: translate('personal.type'),
          dataIndex: 'title',
          width: 400
        },
        {
          title: translate('personal.amount'),
          dataIndex: 'amount',
          render: (num: number) => <div>{(num || 0) / 100}</div>,
          width: 250
        },
        {
          title: translate('personal.order_number'),
          dataIndex: 'pay_order_id',
          width: 300
        },
        {
          title: translate('personal.status'),
          dataIndex: 'status',
          width: 200,
          render: (status: number) => (
            <>
              {status === 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0 4px' }}>
                  <Badge color="blue" text={translate('personal.pending_payment')} />
                  <Popover
                    content={
                      <div>
                        <div>
                          {translate('personal.pending_payment_note', {
                            minutes: <strong className="red">25</strong>
                          })}
                        </div>
                        <div>{translate('personal.voucher_note')}</div>
                      </div>
                    }
                    placement="bottom"
                  >
                    <InfoCircleOutlined />
                  </Popover>
                </div>
              )}
              {status === 2 && <Badge color="green" text={translate('personal.paid')} />}
              {status === 3 && <Badge color="volcano" text={translate('personal.payment_failed')} />}
              {status === 4 && <Badge color="orange" text={translate('personal.expired')} />}
            </>
          )
        },
        {
          title: translate('personal.time'),
          dataIndex: 'create_time',
          width: 300,
          render: (time: number) => <div>{dayjs(time * 1000).format('YYYY-MM-DD HH:mm')}</div>
        }
      ]
    }
  }, [tabIndx, translate])

  return (
    <div className="setting-table">
      <div className="tabs">
        <span className={`text ${tabIndx === 0 ? 'actived' : ''}`} onClick={() => setTabIndx(0)}>
          {translate('personal.points_transactions')}
        </span>
        <span className="split">/</span>
        <span className={`text ${tabIndx === 1 ? 'actived' : ''}`} onClick={() => setTabIndx(1)}>
          {translate('personal.membership_orders')}
        </span>
        <span className="split">/</span>
        <span className={`text ${tabIndx === 2 ? 'actived' : ''}`} onClick={() => setTabIndx(2)}>
          {translate('personal.points_orders')}
        </span>
      </div>
      <Table
        pagination={
          tableData?.total > 8
            ? {
                pageSize: 8,
                showSizeChanger: false,
                total: tableData.total,
                onChange: onPageChange
              }
            : false
        }
        columns={columns}
        locale={{
          emptyText: <div style={{ padding: '40px 0' }}>{translate('personal.no_records')}</div>
        }}
        dataSource={tableData?.list || []}
      ></Table>
    </div>
  )
}

export default memo(PointDetail)
